/**
 * 公司 Premium 方案定價（單一來源）
 * UI 僅展示 DISPLAY_PREMIUM_PLAN_IDS（月費、年費）；季費保留供舊訂閱與後台相容。
 */

export type PremiumPlanId = 'monthly' | 'quarterly' | 'yearly';

export interface PremiumPlanConfig {
  id: PremiumPlanId;
  name: string;
  price: number;
  durationDays: number;
  savingsPercent?: number;
  popular?: boolean;
  features: string[];
  billingLabel: string;
}

export const PREMIUM_PLAN_CONFIG: Record<PremiumPlanId, PremiumPlanConfig> = {
  monthly: {
    id: 'monthly',
    name: '月費方案',
    price: 999,
    durationDays: 30,
    billingLabel: '每月',
    features: [
      '解鎖全部公司端功能（儀表板、案件、員工、收入）',
      '優先顯示在搜尋結果（標準優先級）',
      '基礎數據分析（月報表）',
    ],
  },
  quarterly: {
    id: 'quarterly',
    name: '季費方案',
    price: 2499,
    durationDays: 90,
    savingsPercent: 17,
    billingLabel: '每季',
    features: [
      '解鎖全部公司端功能',
      '優先顯示在搜尋結果（高優先級）',
      '進階數據分析（週報表 + 趨勢分析）',
      '節省 17% 訂閱費用',
    ],
  },
  yearly: {
    id: 'yearly',
    name: '年費方案',
    price: 8999,
    durationDays: 365,
    savingsPercent: 25,
    popular: true,
    billingLabel: '每年',
    features: [
      '解鎖全部公司端功能',
      '優先顯示在搜尋結果（最高優先級 + 置頂）',
      '進階數據分析報告',
      '節省 25% 訂閱費用（相較月付）',
      '優先案件推薦',
    ],
  },
};

/** 前台可選方案（A：僅月費、年費） */
export const DISPLAY_PREMIUM_PLAN_IDS: PremiumPlanId[] = ['monthly', 'yearly'];

export const DISPLAY_PREMIUM_PLANS = DISPLAY_PREMIUM_PLAN_IDS.map(
  (id) => PREMIUM_PLAN_CONFIG[id]
);

export function getPremiumPlanPrice(planType: PremiumPlanId): number {
  return PREMIUM_PLAN_CONFIG[planType].price;
}

export function getPremiumPlanDurationDays(planType: PremiumPlanId): number {
  return PREMIUM_PLAN_CONFIG[planType].durationDays;
}

export function getPremiumPlanLabel(planType: string): string {
  const plan = PREMIUM_PLAN_CONFIG[planType as PremiumPlanId];
  if (plan) return plan.name;
  if (planType === 'trial') return '免費試用';
  return planType;
}

export function getPremiumValueMessage(planType: PremiumPlanId): string {
  const messages: Record<PremiumPlanId, string> = {
    monthly: '已解鎖全部公司端功能！',
    quarterly: '已解鎖全部公司端功能，季付更划算！',
    yearly: '已解鎖全部公司端功能，年付最省！',
  };
  return messages[planType];
}

export function inferPremiumPlanFromDaysRemaining(
  daysRemaining: number
): PremiumPlanId {
  if (daysRemaining >= 300) return 'yearly';
  if (daysRemaining >= 60) return 'quarterly';
  return 'monthly';
}

/** 訂閱 plan_type（含 trial）對應到可選方案 id */
export function normalizeSubscriptionPlanId(
  planType: string | null | undefined
): PremiumPlanId | null {
  if (!planType) return null;
  if (planType === 'trial') return 'monthly';
  if (planType in PREMIUM_PLAN_CONFIG) return planType as PremiumPlanId;
  return null;
}

/** Modal 開啟時預選方案 */
export function resolveModalSelectedPlan(
  activePlanId: PremiumPlanId | null,
  options: { initialPlanId?: PremiumPlanId; renewSamePlan?: boolean }
): PremiumPlanConfig {
  const { initialPlanId, renewSamePlan } = options;

  if (
    renewSamePlan &&
    activePlanId &&
    DISPLAY_PREMIUM_PLAN_IDS.includes(activePlanId)
  ) {
    return PREMIUM_PLAN_CONFIG[activePlanId];
  }

  if (initialPlanId && initialPlanId !== activePlanId) {
    return PREMIUM_PLAN_CONFIG[initialPlanId];
  }

  const alternate = DISPLAY_PREMIUM_PLANS.find((p) => p.id !== activePlanId);
  return alternate ?? DISPLAY_PREMIUM_PLANS[0];
}
